import { createFileRoute, redirect, Outlet, useMatches } from '@tanstack/react-router';
import { isLoggedIn, getDefaultCustomer } from '../Services/UserService';
import { selectedCustomersAtom, getSelectedCustomersFromIndexedDb } from '../store/jotai/customers';
import SocketConnection from '../components/SocketConnection';
import UserData from '../components/UserData';
import NavigatorData from '../components/NavigatorData';
import Customers from '../components/Customers';
import AppShell from '../components/AppShell';
import { AuthenicatedRouteContext } from '../router/types';
import { store } from '../store/jotai/JotaiStore';

/**
 * Authenticated Layout Route
 *
 * This is a layout route (prefix: _) that wraps all authenticated pages.
 * It checks authentication before loading any child routes.
 *
 * Layout routes with _ prefix don't add to the URL path, so:
 * - /alarm/ is still accessed at /alarm/
 * - But it goes through /_authenticated/alarm/ internally
 *
 * Benefits:
 * - Single auth check for all authenticated routes
 * - Automatic redirect to login for unauthenticated users
 * - Shared logic can be added here (e.g., token refresh, user data loading)
 *
 * Child routes can customize the layout via context:
 * export const Route = createFileRoute('/_authenticated/some-route')({
 *   context: {
 *     showSidebar: false,
 *     pageTheme: 'dark',
 *   },
 *   component: SomeComponent,
 * });
 */
export const Route = createFileRoute('/_authenticated')({
  beforeLoad: async ({ context }) => {
    // Check if user is logged in
    if (!isLoggedIn()) {
      throw redirect({ to: '/login' });
    }

    // Get selected customers for permission checks in child routes
    // Try IndexedDB first, fall back to localStorage
    const customersFromState = store.get(selectedCustomersAtom);
    const customersFromIndexedDb = await getSelectedCustomersFromIndexedDb();
    const selectedCustomers =
      customersFromState.length > 0
        ? customersFromState
        : customersFromIndexedDb && customersFromIndexedDb.length > 0
          ? customersFromIndexedDb
          : getDefaultCustomer() || [];

    // Return context that will be available to all child routes.
    // Reset layout chrome flags here so a previous sibling route (e.g. /maps with
    // isNavigatorEnabled: true) cannot leak into /corrections via spread context.
    return {
      ...context,
      selectedCustomers,
      isNavigatorEnabled: false,
      isMenuDisabled: false,
      isRidebook: false,
    };
  },
  loader: async () => {
    // Load main menu data
    // const mainMenuData = await queryClient.ensureQueryData<Promise<IMenuItemsApiResponse | false>>({
    //   queryKey: ['mainMenu'],
    //   queryFn: () => getMenuItems(),
    //   staleTime: Infinity,
    // });
    // let mainMenuItems: IMainMenuItem[] = [];
    // if (mainMenuData && mainMenuData.panel && mainMenuData.panel.items) {
    //   mainMenuItems = mainMenuData.panel.items as IMainMenuItem[];
    // }
    // return {
    //   mainMenuItems,
    // };
  },
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  component: AuthenticatedLayout,
});

/**
 * Layout component that renders child routes
 * Reads context from child routes to customize behavior
 */
function AuthenticatedLayout() {
  // Get all matched routes in the hierarchy
  const matches = useMatches();

  // Get the deepest (most specific) route - this is the current page
  const currentRoute = matches[matches.length - 1];

  // Access the context from the current route
  const routeContext = currentRoute?.context as AuthenicatedRouteContext | undefined;

  // Use context to customize layout.
  // Navigator is OPT-IN: only routes that set isNavigatorEnabled show it (matches the redesign,
  // where most routes used <Layout> without a navigator and only a few used <LayoutWithNavigator>).
  const showMenu = routeContext?.isMenuDisabled !== true;
  const showNavigator = routeContext?.isNavigatorEnabled === true;
  const isRidebook = routeContext?.isRidebook ?? false;

  // document.title + page-view tracking are handled centrally by <RouteTitle> (rendered in __root)
  // from each route's `documentTitle` context.

  // Don't render socket/data providers on trip-map page to avoid conflicts
  const isTripMapPage = window.location.href.includes('trip-map');

  return (
    <>
      {/* Auth-specific data providers - only render when authenticated */}
      {/* These components manage WebSocket, user data, navigator data, and customers */}
      {!isTripMapPage && (
        <>
          <UserData />
          <SocketConnection />
          <NavigatorData />
          <Customers />
        </>
      )}

      <AppShell showMenu={showMenu} showNavigator={showNavigator} isRideBook={isRidebook}>
        <Outlet />
      </AppShell>
    </>
  );
}
